import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Radio,
  CircularProgress,
} from "@mui/material";
import Button from "../../../../../core/ui/Button";
import {
  EmailIcon,
  ProfileIcon,
  SearchIcons,
} from "../../../../../core/global/Icons";

const parseAvatar = (profileStr) => {
  try {
    return JSON.parse(profileStr)?.profile || null;
  } catch {
    return null;
  }
};

const EmployeeSelectionModal = ({ open, onClose, onConfirm, companyUsers = [], loadingUsers = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const mappedEmployees = useMemo(
    () =>
      companyUsers.map((u) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name}`,
        position: u.name || "",
        email: u.email,
        avatar: parseAvatar(u.profile),
      })),
    [companyUsers]
  );

  const filteredEmployees = useMemo(
    () =>
      mappedEmployees.filter((emp) =>
        `${emp.name} ${emp.email}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ),
    [mappedEmployees, searchQuery]
  );

  const handleConfirm = () => {
    const selectedEmployee = mappedEmployees.find(
      (e) => e.id === selectedEmployeeId
    );
    if (selectedEmployee) {
      onConfirm(selectedEmployee);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedEmployeeId(null);
    setSearchQuery("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          padding: "24px",
          maxWidth: "1080px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
          color: "#292C2F",
          padding: 0,
          marginBottom: "24px",
        }}
      >
        Referred to:
      </DialogTitle>
      <DialogContent
        sx={{ padding: 0, marginBottom: "24px", overflow: "hidden" }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search Employee Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcons
                  sx={{ color: "#283745", width: "20px", height: "20px" }}
                />
              </InputAdornment>
            ),
            sx: {
              height: "40px",
              backgroundColor: "#F7F8F8",
              padding: "8px 12px",
              width: "456px",
              borderRadius: "6px",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "24px",
              "& fieldset": {
                border: "none",
              },
              "& input": {
                padding: 0,
                paddingLeft: "12px",
                color: "#292C2F",
              },
              "& input::placeholder": {
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#8F969D",
                opacity: 1,
              },
            },
          }}
        />
        <Box
          sx={{
            maxHeight: "350px",
            overflowY: "auto",
            marginTop: "24px",
            paddingRight: "5px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#D6D9DB",
              borderRadius: "3px",
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {loadingUsers ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} sx={{ color: "#003F2E" }} />
              </Box>
            ) : filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp) => (
                <Box
                  key={emp.id}
                  onClick={() => setSelectedEmployeeId(emp.id)}
                  sx={{
                    padding: "16px",
                    borderRadius: "12px",
                    border:
                      selectedEmployeeId === emp.id
                        ? "2px solid #11783C"
                        : "1px solid #D6D9DB",
                    backgroundColor:
                      selectedEmployeeId === emp.id ? "#F0F9F4" : "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#11783C",
                      backgroundColor: "#F0F9F4",
                    },
                  }}
                >
                  <Radio
                    checked={selectedEmployeeId === emp.id}
                    onChange={() => setSelectedEmployeeId(emp.id)}
                    sx={{
                      padding: 0,
                      color: "#D6D9DB",
                      "&.Mui-checked": {
                        color: "#003F2E",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "20px",
                      },
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      border: "2px solid #D6D9DB",
                      bgcolor: "#E0E0E0",
                    }}
                    src={emp.avatar}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "24px",
                        color: "#292C2F",
                      }}
                    >
                      {emp.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          color: "#5B6167",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <ProfileIcon />
                        {emp.position ? `(${emp.position})` : "asdasd"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          color: "#5B6167",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <EmailIcon />
                        {emp.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  sx={{
                    color: "#8F969D",
                    fontSize: "14px",
                    fontFamily: "Inter",
                  }}
                >
                  No employees found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 0, gap: "12px" }}>
        <Button
          onClick={handleConfirm}
          disabled={!selectedEmployeeId}
          variant="dark"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Assign
        </Button>
        <Button
          onClick={handleClose}
          variant="neutral"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeSelectionModal;
